import { useAdvancedEnabled } from '../useAdvancedEnabled'
import { advancedGet } from './client'
import { AdvancedPatientSummary } from './types'
import { AdvancedResourceState, useAdvancedResource } from './useAdvancedResource'
import { useRefreshWhileVisible } from './useRefreshWhileVisible'

export type CaseloadState =
  | { status: 'disabled' | 'loading' | 'error' }
  | { status: 'ready'; byUid: Record<string, AdvancedPatientSummary> }

type PatientsResponse = { patients: AdvancedPatientSummary[] }

export const CASELOAD_TIMEOUT_MS = 5000

const POLL_MS = 30_000

const fetchCaseload = (signal: AbortSignal) =>
  new Promise<PatientsResponse>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('advanced caseload timed out')), CASELOAD_TIMEOUT_MS)
    advancedGet<PatientsResponse>('/patients', signal)
      .then(resolve, reject)
      .finally(() => clearTimeout(timer))
  })

const toCaseload = (enabled: boolean, resource: AdvancedResourceState<PatientsResponse>) => {
  if (!enabled) {
    return { status: 'disabled' } as const
  }

  if (resource.status === 'ready') {
    return {
      status: 'ready',
      byUid: Object.fromEntries(resource.data.patients.map((patient) => [patient.uid, patient])),
    } as const
  }

  return { status: resource.status === 'error' ? 'error' : 'loading' } as const
}

// Re-reads in place, so the care-team chat unread counts on the rows stay current.
export const useAdvancedCaseloadFetch = (): { caseload: CaseloadState; refresh: () => void } => {
  const enabled = useAdvancedEnabled()

  const resource = useAdvancedResource<PatientsResponse>(fetchCaseload, [enabled], !enabled)
  useRefreshWhileVisible(resource.retry, POLL_MS)

  return { caseload: toCaseload(enabled, resource), refresh: resource.retry }
}
